// src/pages/help/Documentacao.jsx
import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search, BookOpen, ChevronRight, ChevronDown, Home, Building,
  Users, DollarSign, CheckSquare, MessageCircle, Calendar,
  FileText, BarChart3, Settings, Globe, Sparkles, Zap,
  Smartphone, Shield, Wifi, WifiOff, Palette, ExternalLink,
  Copy, X, Menu, Star, Award, Crown, ArrowUp, Hash
} from "lucide-react";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { useAuth } from "../../contexts/useAuth";

// ============================================================
// 🎨 BRANDING - AZUL PREMIUM + DORADO
// ============================================================
const BRAND = {
  primary: "#1a56db",      // Azul premium
  primaryLight: "#3b82f6",
  primaryDark: "#1e3a5f",
  gold: "#c8a84e",          // Dorado
  goldLight: "#e2c97e",
  goldDark: "#8b6914",
  bg: "var(--bg-primary)",
  card: "rgba(255,255,255,0.9)",
  text: "#1e293b",
  textSecondary: "#64748b",
};

// ============================================================
// 📚 ESTRUTURA DA DOCUMENTAÇÃO
// ============================================================
const DOC_SECTIONS = [
  {
    id: "introducao",
    icon: BookOpen,
    title: "Introdução",
    gold: true,
    subsections: [
      { id: "bem-vindo", title: "Bem-vindo ao Ilha Leads", content: "O Ilha Leads é um CRM completo e inteligente para imobiliárias, corretores e administradores de propriedades. Com tecnologia de ponta, oferece gestão de imóveis, clientes, financeiro, tarefas, mensagens WhatsApp e muito mais — tudo integrado em uma plataforma moderna, rápida e que funciona até offline." },
      { id: "diferenciais", title: "Diferenciais Exclusivos", content: "✅ Offline-first: funciona sem internet\n✅ PWA: instale como app no celular\n✅ White-label: personalize com sua marca\n✅ IA integrada: recomendações inteligentes\n✅ Multi-dispositivo: sincronização perfeita\n✅ Relatórios profissionais em PDF e Excel\n✅ Vitrine pública de imóveis com SEO" },
      { id: "acesso", title: "Como Acessar", content: "1. Acesse app.ilhaleads.com.br\n2. Faça login com seu email e senha\n3. Complete o onboarding\n4. Comece a usar!" },
    ]
  },
  {
    id: "dashboard",
    icon: Home,
    title: "Dashboard",
    subsections: [
      { id: "visao-geral", title: "Visão Geral", content: "O Dashboard é sua central de comando. Aqui você vê:\n• KPIs principais (imóveis, clientes, receita)\n• Gráficos de vendas e aluguéis\n• Tarefas pendentes e vencidas\n• Métricas de WhatsApp\n• Crescimento mensal" },
      { id: "filtros", title: "Filtros e Períodos", content: "Alterne entre visualização de Vendas e Aluguéis usando as abas no topo do gráfico. Os dados são atualizados em tempo real." },
    ]
  },
  {
    id: "imoveis",
    icon: Building,
    title: "Gestão de Imóveis",
    subsections: [
      { id: "cadastro", title: "Cadastrar Imóvel", content: "1. Clique em 'Novo Imóvel'\n2. Preencha: título, preço, localização, tipo, status\n3. Adicione fotos (múltiplas, com preview)\n4. Configure detalhes: quartos, banheiros, área\n5. Salve — o imóvel aparece na vitrine pública automaticamente" },
      { id: "vitrine", title: "Vitrine Pública", content: "Seus imóveis são exibidos em uma página pública profissional com:\n• Filtros avançados\n• Galeria de imagens\n• Compartilhamento via WhatsApp\n• SEO otimizado para Google\n• Recomendações inteligentes de imóveis similares" },
      { id: "status", title: "Status do Imóvel", content: "Disponível, Alugado, Vendido, Em negociação, Reservado. Cada status gera métricas específicas no dashboard e relatórios." },
    ]
  },
  {
    id: "clientes",
    icon: Users,
    title: "Gestão de Clientes",
    subsections: [
      { id: "cadastro-cliente", title: "Cadastrar Cliente", content: "1. Clique em 'Novo Cliente'\n2. Preencha: nome, email, telefone\n3. Vincule imóveis de interesse\n4. Adicione notas e tags\n5. Acompanhe no pipeline" },
      { id: "pipeline", title: "Pipeline de Vendas", content: "Acompanhe cada cliente pelo funil:\n• Lead → Contato → Visita → Proposta → Fechamento\n• Arraste cards para mudar de estágio\n• Notas e histórico completo" },
      { id: "whatsapp", title: "Integração WhatsApp", content: "• Envie mensagens direto do sistema\n• Histórico completo de conversas\n• Templates de mensagens rápidas\n• Agendamento de disparos" },
    ]
  },
  {
    id: "financeiro",
    icon: DollarSign,
    title: "Financeiro",
    subsections: [
      { id: "lancamentos", title: "Lançamentos", content: "• Receitas: vendas, aluguéis, comissões\n• Despesas: custos operacionais, marketing\n• Categorização completa\n• Filtro por mês e tipo" },
      { id: "graficos", title: "Gráficos e Análises", content: "• Barras: vendas mensais\n• Linhas: evolução de aluguéis\n• Cards: receitas, despesas, saldo\n• Métricas: ticket médio, crescimento" },
      { id: "relatorios", title: "Relatórios Exportáveis", content: "Exporte relatórios completos em:\n• PDF profissional com gráficos\n• Excel para análise detalhada\n• Compartilhamento via WhatsApp\n• Período personalizável" },
    ]
  },
  {
    id: "tarefas",
    icon: CheckSquare,
    title: "Tarefas e Calendário",
    subsections: [
      { id: "criar-tarefa", title: "Criar Tarefa", content: "1. Clique em 'Nova Tarefa'\n2. Defina: título, data, prioridade\n3. Vincule a cliente ou imóvel\n4. Acompanhe status: Pendente → Em andamento → Concluída" },
      { id: "calendario", title: "Calendário", content: "Visualize todas as tarefas e eventos em:\n• Visão mensal, semanal ou diária\n• Eventos de imóveis (visitas, vistorias)\n• Lembretes automáticos" },
    ]
  },
  {
    id: "mensagens",
    icon: MessageCircle,
    title: "Mensagens e WhatsApp",
    subsections: [
      { id: "envio", title: "Envio de Mensagens", content: "• Disparo individual ou em massa\n• Templates personalizáveis\n• Agendamento de envios\n• Relatórios de engajamento" },
      { id: "metricas", title: "Métricas de Comunicação", content: "Acompanhe:\n• Mensagens enviadas (hoje, semana, mês)\n• Clientes contactados\n• Taxa de resposta\n• Melhor horário de envio" },
    ]
  },
  {
    id: "ia",
    icon: Sparkles,
    title: "Inteligência Artificial",
    gold: true,
    subsections: [
      { id: "recomendacoes", title: "Recomendações Inteligentes", content: "O sistema analisa o comportamento do cliente e recomenda imóveis similares automaticamente, aumentando as chances de venda." },
      { id: "precos", title: "Inteligência de Preços", content: "Análise de mercado para sugerir o preço ideal do imóvel baseado em:\n• Localização\n• Características\n• Preços da região\n• Tendências de mercado" },
      { id: "urgencia", title: "Gatilhos de Urgência", content: "Badges automáticos que destacam:\n• 'Alta demanda'\n• 'Últimas unidades'\n• 'Preço reduzido'\n• 'Novidade'" },
    ]
  },
  {
    id: "white-label",
    icon: Palette,
    title: "White Label",
    subsections: [
      { id: "personalizacao", title: "Personalização", content: "Configure sua marca:\n• Logo e nome da imobiliária\n• Cores primária e secundária\n• Slogan e descrição\n• Rodapé personalizado\n• URL própria (domínio customizado)" },
    ]
  },
  {
    id: "offline",
    icon: WifiOff,
    title: "Modo Offline",
    subsections: [
      { id: "funcionamento", title: "Como Funciona", content: "O Ilha Leads foi projetado com tecnologia Offline-First:\n\n📴 Sem internet:\n• Todos os dados ficam salvos no dispositivo\n• Você cadastra imóveis, clientes e transações normalmente\n• As fotos são armazenadas localmente\n\n🌐 Quando conectar:\n• Sincronização automática com a nuvem\n• Dados enviados para o Firestore\n• Disponível em todos os seus dispositivos\n\n⚡ Vantagem:\n• Funciona em áreas rurais e praias\n• Nunca perde uma venda por falta de sinal\n• Ideal para visitas técnicas e vistorias" },
    ]
  },
  {
    id: "pwa",
    icon: Smartphone,
    title: "App Mobile (PWA)",
    subsections: [
      { id: "instalar", title: "Como Instalar", content: "1. Acesse app.ilhaleads.com.br pelo celular\n2. Abra o menu do navegador (⋮)\n3. Clique em 'Adicionar à tela inicial'\n4. O app será instalado como nativo!\n\n✅ Funciona em Android e iOS\n✅ Ocupa menos de 5MB\n✅ Atualiza automaticamente\n✅ Ícone na tela inicial\n✅ Notificações push" },
    ]
  },
  {
    id: "seguranca",
    icon: Shield,
    title: "Segurança",
    subsections: [
      { id: "dados", title: "Proteção de Dados", content: "• Autenticação Firebase (Google)\n• Dados criptografados em trânsito (HTTPS)\n• Firestore Rules para isolamento multi-tenant\n• Cada imobiliária acessa apenas seus dados\n• Backup automático na nuvem\n• Conformidade LGPD" },
    ]
  },
];

// ============================================================
// 🧩 COMPONENTE PRINCIPAL
// ============================================================
export default function Documentacao() {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [activeSection, setActiveSection] = useState("introducao");
  const [expandedSubsections, setExpandedSubsections] = useState({});
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Filtrar seções pela busca
  const filteredSections = useMemo(() => {
    if (!search.trim()) return DOC_SECTIONS;
    const q = search.toLowerCase();
    return DOC_SECTIONS.map(section => ({
      ...section,
      subsections: section.subsections.filter(sub =>
        sub.title.toLowerCase().includes(q) ||
        sub.content.toLowerCase().includes(q)
      )
    })).filter(s => s.subsections.length > 0);
  }, [search]);

  // Toggle subseção
  const toggleSubsection = (sectionId, subId) => {
    const key = `${sectionId}-${subId}`;
    setExpandedSubsections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Seção ativa baseada no scroll
  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`section-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: BRAND.bg }}>
      
      {/* ═══════════════════════════════════════════════════════ */}
      {/* HEADER PREMIUM */}
      {/* ═══════════════════════════════════════════════════════ */}
      <header 
        className="sticky top-0 z-50 backdrop-blur-xl border-b shadow-sm"
        style={{ 
          background: `linear-gradient(135deg, ${BRAND.primaryDark} 0%, ${BRAND.primary} 100%)`,
          borderColor: `${BRAND.gold}30`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Botão menu mobile */}
            {isMobile && (
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white p-1">
                <Menu size={22} />
              </button>
            )}
            
            {/* Logo + Título */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})` }}>
                <BookOpen size={16} className="text-white" />
              </div>
              <div>
                <h1 className="font-bold text-white text-lg flex items-center gap-2">
                  Central de Ajuda
                  <Star size={14} style={{ color: BRAND.gold }} fill={BRAND.gold} />
                </h1>
                {!isMobile && (
                  <p className="text-xs text-white/70">Documentação completa do sistema</p>
                )}
              </div>
            </div>
          </div>

          {/* Barra de busca */}
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
            <input
              type="text"
              placeholder={isMobile ? "Buscar..." : "Buscar na documentação..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 rounded-xl text-sm text-white placeholder-white/50 outline-none transition-all"
              style={{ 
                background: "rgba(255,255,255,0.1)", 
                border: `1px solid rgba(255,255,255,0.15)`,
                width: isMobile ? 140 : 280
              }}
            />
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* CONTEÚDO PRINCIPAL */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto flex">
        
        {/* ── SIDEBAR ── */}
        <AnimatePresence>
          {(!isMobile || sidebarOpen) && (
            <motion.aside
              initial={isMobile ? { x: -300 } : false}
              animate={isMobile ? { x: 0 } : {}}
              exit={isMobile ? { x: -300 } : {}}
              className={`${isMobile ? 'fixed inset-0 z-40 pt-16 bg-card/95 backdrop-blur-xl overflow-auto' : 'sticky top-16 w-72 h-[calc(100vh-4rem)] overflow-auto border-r'} flex-shrink-0`}
              style={{ borderColor: `${BRAND.gold}20` }}
            >
              {/* Overlay mobile */}
              {isMobile && (
                <div className="p-4 border-b flex justify-between items-center" style={{ borderColor: `${BRAND.gold}20` }}>
                  <span className="font-bold text-sm" style={{ color: BRAND.primary }}>📚 Navegação</span>
                  <button onClick={() => setSidebarOpen(false)}>
                    <X size={18} style={{ color: BRAND.textSecondary }} />
                  </button>
                </div>
              )}

              <nav className="p-4 space-y-1">
                {DOC_SECTIONS.map(section => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left flex items-center gap-2 px-3 py-2 rounded-xl transition-all text-sm font-medium ${
                        isActive ? 'text-white shadow-lg' : 'hover:bg-muted'
                      }`}
                      style={isActive ? { 
                        background: section.gold 
                          ? `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})` 
                          : `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryDark})`
                      } : {}}
                    >
                      <Icon size={16} className={isActive ? 'text-white' : ''} style={!isActive ? { color: section.gold ? BRAND.gold : BRAND.primary } : {}} />
                      <span className="flex-1">{section.title}</span>
                      {section.gold && (
                        <Star size={10} fill={isActive ? 'white' : BRAND.gold} style={{ color: isActive ? 'white' : BRAND.gold }} />
                      )}
                    </button>
                  );
                })}
              </nav>
            </motion.aside>
          )}
        </AnimatePresence>

        {/* ── CONTEÚDO ── */}
        <main className={`flex-1 min-h-screen ${isMobile ? 'p-4' : 'p-8'}`}>
          
          {/* Badge boas-vindas */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-6 rounded-2xl relative overflow-hidden"
            style={{ 
              background: `linear-gradient(135deg, ${BRAND.primary}10, ${BRAND.gold}15)`,
              border: `1px solid ${BRAND.gold}30`
            }}
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldDark})` }}>
                <Award size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold" style={{ color: BRAND.primaryDark }}>
                  Bem-vindo à Central de Ajuda, {user?.name?.split(' ')[0] || 'Usuário'}!
                </h2>
                <p className="mt-1 text-sm" style={{ color: BRAND.textSecondary }}>
                  Tudo que você precisa saber para dominar o Ilha Leads e vender mais imóveis. 
                  Navegue pelo menu lateral ou use a busca acima.
                </p>
              </div>
            </div>
            {/* Decoração */}
            <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10"
              style={{ background: `radial-gradient(circle, ${BRAND.gold}, transparent)` }} />
          </motion.div>

          {/* Seções */}
          {filteredSections.map(section => {
            const Icon = section.icon;
            return (
              <motion.div
                key={section.id}
                id={`section-${section.id}`}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className="mb-10"
              >
                {/* Título da seção */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-xl" style={{ 
                    background: section.gold 
                      ? `linear-gradient(135deg, ${BRAND.gold}20, ${BRAND.goldLight}30)` 
                      : `linear-gradient(135deg, ${BRAND.primary}15, ${BRAND.primaryLight}20)` 
                  }}>
                    <Icon size={22} style={{ color: section.gold ? BRAND.goldDark : BRAND.primary }} />
                  </div>
                  <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: BRAND.text }}>
                    {section.title}
                    {section.gold && <Crown size={16} style={{ color: BRAND.gold }} fill={BRAND.gold} />}
                  </h2>
                </div>

                {/* Subseções (accordion) */}
                <div className="space-y-3 ml-11">
                  {section.subsections.map(sub => {
                    const key = `${section.id}-${sub.id}`;
                    const isOpen = expandedSubsections[key] || search.trim().length > 0;
                    
                    return (
                      <div
                        key={sub.id}
                        className="rounded-xl overflow-hidden border"
                        style={{ 
                          background: BRAND.card, 
                          borderColor: `${section.gold ? BRAND.gold : BRAND.primary}20`,
                        }}
                      >
                        <button
                          onClick={() => toggleSubsection(section.id, sub.id)}
                          className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-muted transition-colors"
                        >
                          <span className="font-medium text-sm" style={{ color: BRAND.text }}>
                            {sub.title}
                          </span>
                          <motion.div
                            animate={{ rotate: isOpen ? 180 : 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <ChevronDown size={16} style={{ color: section.gold ? BRAND.gold : BRAND.primary }} />
                          </motion.div>
                        </button>
                        
                        <AnimatePresence>
                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25 }}
                              className="overflow-hidden"
                            >
                              <div className="px-4 pb-4 pt-1">
                                <div className="prose prose-sm max-w-none">
                                  {sub.content.split("\n").map((line, i) => {
                                    // Destacar linhas com ✅
                                    const isHighlight = line.startsWith("✅");
                                    // Destacar linhas com • ou números
                                    const isBullet = line.startsWith("•") || /^\d\./.test(line);
                                    
                                    return (
                                      <p key={i} className={`text-sm leading-relaxed ${
                                        isHighlight ? 'font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded' : ''
                                      }`} style={{ color: isHighlight ? undefined : BRAND.textSecondary }}>
                                        {line}
                                      </p>
                                    );
                                  })}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}

          {/* Footer */}
          <div className="mt-16 pt-8 border-t text-center" style={{ borderColor: `${BRAND.gold}20` }}>
            <p className="text-sm" style={{ color: BRAND.textSecondary }}>
              © {new Date().getFullYear()} Ilha Leads — Todos os direitos reservados
            </p>
            <div className="flex items-center justify-center gap-1 mt-1">
              <Star size={10} style={{ color: BRAND.gold }} fill={BRAND.gold} />
              <span className="text-xs" style={{ color: BRAND.gold }}>Premium Documentation</span>
              <Star size={10} style={{ color: BRAND.gold }} fill={BRAND.gold} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}